/*
 * Calculo de frete por CEP dentro da gaveta do carrinho.
 *
 * Por que existe a tabela de UF abaixo:
 * /cart/shipping_rates.json exige shipping_address[province] junto do CEP —
 * sem ele a resposta e 422 {"province":["Selecione um estado"]}. Testado.
 * Como o cliente digita so o CEP, a UF sai da faixa do proprio numero, o que
 * evita um segundo campo e evita depender de um servico externo de CEP.
 *
 * As faixas sao as dos Correios. Onde um estado tem faixas descontinuas
 * (AM e RR intercalados, DF e GO intercalados) cada trecho vira uma linha.
 */

const FAIXAS_CEP = [
  [1000000, 5999999, 'SP'], [6000000, 6999999, 'SP'],
  [7000000, 9999999, 'SP'], [11000000, 19999999, 'SP'],
  [20000000, 26600999, 'RJ'], [26601000, 28999999, 'RJ'],
  [29000000, 29999999, 'ES'],
  [30000000, 39999999, 'MG'],
  [40000000, 48999999, 'BA'],
  [49000000, 49999999, 'SE'],
  [50000000, 56999999, 'PE'],
  [57000000, 57999999, 'AL'],
  [58000000, 58999999, 'PB'],
  [59000000, 59999999, 'RN'],
  [60000000, 63999999, 'CE'],
  [64000000, 64999999, 'PI'],
  [65000000, 65999999, 'MA'],
  [66000000, 68899999, 'PA'],
  [68900000, 68999999, 'AP'],
  [69000000, 69299999, 'AM'],
  [69300000, 69399999, 'RR'],
  [69400000, 69899999, 'AM'],
  [69900000, 69999999, 'AC'],
  [70000000, 72799999, 'DF'],
  [72800000, 72999999, 'GO'],
  [73000000, 73699999, 'DF'],
  [73700000, 76799999, 'GO'],
  [76800000, 76999999, 'RO'],
  [77000000, 77999999, 'TO'],
  [78000000, 78899999, 'MT'],
  [78900000, 78999999, 'RO'],
  [79000000, 79999999, 'MS'],
  [80000000, 87999999, 'PR'],
  [88000000, 89999999, 'SC'],
  [90000000, 99999999, 'RS'],
];

const ufDoCep = (digitos) => {
  const n = Number(digitos);
  const faixa = FAIXAS_CEP.find(([ini, fim]) => n >= ini && n <= fim);
  return faixa ? faixa[2] : null;
};

const somenteDigitos = (v) => (v || '').replace(/\D/g, '');

const formatarCep = (v) => {
  const d = somenteDigitos(v).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

const formatarReais = (valor) =>
  Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/*
 * A API /cart/shipping_rates.json calcula sobre o CARRINHO, nunca sobre a peca
 * da tela. Na PDP com a sacola vazia ela responde sem taxa nenhuma, e a cliente
 * batia num "adicione a peca a sacola" bem no momento em que o frete mais pesa
 * na decisao. Reportado pela Estela no review de 01/09.
 *
 * Aqui a peca entra por um instante, as taxas sao lidas e ela sai. Nao pedimos
 * `sections` em nenhuma das chamadas, entao o tema nao re-renderiza nada e o
 * contador do header nao pisca. Se a sacola JA tem itens nada e tocado: a taxa
 * e plana e nao muda com o conteudo (medido em 80, 240, 320 e 400 reais).
 *
 * Devolve { dados, simulou } — `simulou` diz se a taxa saiu da peca da tela ou
 * do que ja estava na sacola, porque o rodape do resultado muda conforme isso.
 */
async function comAPecaNaSacola(buscar) {
  let chave = null;

  try {
    let vazia = false;
    try {
      const carrinho = await (await fetch('/cart.js')).json();
      vazia = carrinho.item_count === 0;
    } catch {
      /* sem /cart.js nao da para saber; segue sem simular */
    }

    if (vazia) {
      const campo = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      const idVariante = campo && campo.value;
      if (idVariante) {
        try {
          const resp = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: Number(idVariante), quantity: 1 }),
          });
          /* tamanho esgotado devolve 422 — nao e erro, so nao da para simular */
          if (resp.ok) chave = (await resp.json()).key;
        } catch {
          /* rede caiu no meio: segue sem simular, o texto honesto cobre */
        }
      }
    }

    return { dados: await buscar(), simulou: !!chave };
  } finally {
    if (chave) {
      /* sai pela chave da linha, nao pelo id da variante: se a cliente tinha
         a mesma peca com propriedades, o id casaria com a linha errada. */
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: chave, quantity: 0 }),
        });
      } catch {
        /* nada a fazer: a peca fica na sacola e a cliente ve e remove */
      }
    }
  }
}

function iniciar(raiz) {
  if (!raiz || raiz.dataset.dzPronto) return;
  raiz.dataset.dzPronto = '1';

  const abrir = raiz.querySelector('[data-dz-cep-abrir]');
  const painel = raiz.querySelector('[data-dz-cep-painel]');
  const campo = raiz.querySelector('[data-dz-cep-campo]');
  const calcular = raiz.querySelector('[data-dz-cep-calcular]');
  const resultado = raiz.querySelector('[data-dz-cep-resultado]');
  if (!abrir || !painel || !campo || !calcular || !resultado) return;

  // o CEP digitado sobrevive ao re-render da gaveta
  const salvo = sessionStorage.getItem('dz-cep');
  if (salvo) campo.value = formatarCep(salvo);

  abrir.addEventListener('click', () => {
    const aberto = painel.hasAttribute('hidden');
    painel.toggleAttribute('hidden', !aberto);
    abrir.setAttribute('aria-expanded', String(aberto));
    if (aberto) campo.focus();
  });

  campo.addEventListener('input', () => {
    campo.value = formatarCep(campo.value);
  });

  campo.addEventListener('keydown', (e) => {
    // o campo vive fora do form do checkout, mas Enter tem que calcular
    if (e.key === 'Enter') {
      e.preventDefault();
      calcular.click();
    }
  });

  const mostrar = (html, tipo = '') => {
    resultado.className = `dz-cep__resultado${tipo ? ` dz-cep__resultado--${tipo}` : ''}`;
    resultado.innerHTML = html;
  };

  calcular.addEventListener('click', async () => {
    const cep = somenteDigitos(campo.value);
    if (cep.length !== 8) {
      mostrar('CEP incompleto. Digite os 8 números.', 'erro');
      return;
    }

    const uf = ufDoCep(cep);
    if (!uf) {
      mostrar('Não reconhecemos esse CEP. Confira os números.', 'erro');
      return;
    }

    /*
     * Peca indisponivel nao pode ser simulada, e o motivo nao e obvio: o
     * /cart/add.js ACEITA a variante esgotada (HTTP 200, entra na sacola) e e o
     * /cart/shipping_rates.json que depois devolve [] — o mesmo vazio de "nao
     * entregamos nessa regiao". Sem esta checagem a cliente le "ainda nao
     * entregamos em SP" numa loja que entrega em SP. Medido em top-obsessed-preto.
     *
     * Quem sabe se a variante esta a venda e o proprio tema: ele desabilita o
     * botao de compra. Usamos esse estado em vez de reimplementar a regra.
     */
    if (raiz.classList.contains('dz-cep--produto')) {
      const comprar = document.querySelector('form[action*="/cart/add"] button[name="add"]');
      if (comprar && comprar.hasAttribute('disabled')) {
        mostrar('Escolha um tamanho disponível para simular o frete.');
        return;
      }
    }

    sessionStorage.setItem('dz-cep', cep);
    calcular.setAttribute('disabled', '');
    mostrar('Calculando…');

    try {
      const url =
        '/cart/shipping_rates.json' +
        `?shipping_address%5Bzip%5D=${cep}` +
        '&shipping_address%5Bcountry%5D=Brazil' +
        `&shipping_address%5Bprovince%5D=${uf}`;
      const naPdp = raiz.classList.contains('dz-cep--produto');
      const buscar = async () => {
        const r = await fetch(url);
        return { ok: r.ok, corpo: await r.json() };
      };

      const saida = naPdp ? await comAPecaNaSacola(buscar) : { dados: await buscar(), simulou: false };
      const { ok, corpo: dados } = saida.dados;
      const simulou = saida.simulou;

      if (!ok || !dados.shipping_rates) {
        mostrar('Não foi possível calcular agora. Tente de novo em instantes.', 'erro');
        return;
      }
      if (!dados.shipping_rates.length) {
        /*
         * Sem taxa NAO significa "nao entregamos": a API calcula sobre o
         * carrinho, e um carrinho vazio tambem responde vazio. So da para dar
         * a noticia ruim quando existia peca na sacola no momento da consulta.
         *
         * Cuidado com a ordem: quando a simulacao rodou, a peca JA saiu da
         * sacola aqui (o finally do helper roda antes desta linha). Perguntar
         * ao /cart.js agora devolveria "vazia" e mandaria a cliente adicionar
         * uma peca que ela acabou de ver ser simulada. Por isso quem decide e
         * o `simulou`, nao uma nova consulta ao carrinho.
         */
        if (simulou) {
          /* a peca entrou na sacola e mesmo assim nao veio taxa: ai sim a
             regiao e o problema, porque a disponibilidade ja foi checada. */
          mostrar(`Ainda não entregamos em ${uf}.`, 'erro');
          return;
        }

        let vazio = false;
        try {
          const carrinho = await (await fetch('/cart.js')).json();
          vazio = carrinho.item_count === 0;
        } catch {
          /* se nem o carrinho responde, cai na mensagem generica abaixo */
        }
        if (vazio) {
          /* na PDP so se chega aqui quando a peca nao pode entrar na sacola —
             tamanho esgotado devolve 422 no add */
          mostrar(
            raiz.classList.contains('dz-cep--produto')
              ? 'Escolha um tamanho disponível para simular o frete.'
              : 'Adicione a peça à sacola para ver o frete até você.'
          );
        } else {
          mostrar(`Ainda não entregamos em ${uf}.`, 'erro');
        }
        return;
      }

      const linhas = dados.shipping_rates
        .map((taxa) => {
          const preco = Number(taxa.price);
          const valor = preco === 0
            ? '<strong class="dz-cep__gratis">Grátis</strong>'
            : `<strong>${formatarReais(preco)}</strong>`;
          const prazo = taxa.delivery_days?.length
            ? ` <span class="dz-cep__prazo">em até ${Math.max(...taxa.delivery_days)} dias úteis</span>`
            : '';
          return `<li class="dz-cep__opcao"><span>${taxa.name}</span>${valor}${prazo}</li>`;
        })
        .join('');

      let rodape = '';
      if (naPdp) {
        rodape = simulou
          ? '<p class="dz-cep__obs">Valor do frete para esta peça.</p>'
          : '<p class="dz-cep__obs">Valor referente ao que já está na sacola.</p>';
      }
      mostrar(
        `<p class="dz-cep__uf">Entrega para ${uf}</p>` +
          `<ul class="dz-cep__lista">${linhas}</ul>${rodape}`
      );

      /*
       * A barra acima e calculada em Liquid contra o limiar configurado, mas
       * quem manda e o frete de verdade. Se a transportadora ja devolveu zero
       * para este CEP, a barra nao pode continuar dizendo "faltam R$ X" —
       * seria uma contradicao visivel na mesma tela.
       */
      const jaGratis = dados.shipping_rates.some((t) => Number(t.price) === 0);
      const barra = document.querySelector('.dz-frete');
      if (jaGratis && barra && !barra.classList.contains('dz-frete--liberado')) {
        barra.classList.add('dz-frete--liberado');
        const texto = barra.querySelector('.dz-frete__texto');
        const preenchido = barra.querySelector('.dz-frete__preenchido');
        if (texto) {
          texto.innerHTML =
            '<span class="dz-frete__marca" aria-hidden="true">&check;</span>Frete grátis para o seu CEP';
        }
        if (preenchido) preenchido.style.width = '100%';
      }
    } catch (erro) {
      console.warn('[dz-frete]', erro.message);
      mostrar('Não foi possível calcular agora. Tente de novo em instantes.', 'erro');
    } finally {
      calcular.removeAttribute('disabled');
    }
  });
}

// a gaveta e re-renderizada por innerHTML a cada mudanca do carrinho, entao o
// bloco precisa ser religado toda vez que reaparece
const ligarTudo = () => document.querySelectorAll('[data-dz-cep]').forEach(iniciar);
document.addEventListener('DOMContentLoaded', ligarTudo);
ligarTudo();
new MutationObserver(ligarTudo).observe(document.documentElement, { childList: true, subtree: true });
